import React from 'react';
import { Alert, Button, Popover, Stack, TextField, Typography } from '@mui/material';
import { reportActionSx, reportBodySx, reportPanelSx, reportSectionTitleSx } from './clientReportStyles';

const dateFieldSx = {
  minWidth: 0,
  '& .MuiInputLabel-root': { color: '#64748b', fontSize: '0.8rem', fontWeight: 600 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0f172a' },
  '& .MuiOutlinedInput-root': {
    color: '#334155',
    bgcolor: '#f8fafc',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#0f172a' },
  },
  '& input': { minWidth: 0, fontSize: { xs: '16px', sm: '0.8rem' } },
};

const actionSx = {
  ...reportActionSx,
  minWidth: 72,
  px: 1.5,
  '&.Mui-focusVisible': { outline: '2px solid #2563eb', outlineOffset: 2 },
};

export function businessTodayIso() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Bucharest', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type).value).join('-');
}

export function customPeriodDays(dateFrom, dateTo) {
  return (Date.parse(`${dateTo}T00:00:00Z`) - Date.parse(`${dateFrom}T00:00:00Z`)) / 86400000 + 1;
}

export function validateCustomPeriod(dateFrom, dateTo, maxDays = 366) {
  const days = customPeriodDays(dateFrom, dateTo);
  if (!dateFrom || !dateTo || !Number.isFinite(days)) return 'Choose both a start date and an end date.';
  if (dateFrom > dateTo) return 'The start date must be on or before the end date.';
  if (dateTo > businessTodayIso()) return 'Custom dates cannot be in the future.';
  if (days > maxDays) return `Choose a period of no more than ${maxDays} days.`;
  return '';
}

export default function CustomPeriodPopover({
  id, open, anchorEl, onClose, dateFrom, dateTo, onDateFromChange, onDateToChange,
  onApply, error, fromLabel = 'From', toLabel = 'To',
  description = 'Choose up to 366 days.',
}) {
  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': `${id}-title`,
        sx: {
          ...reportPanelSx,
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          mt: 0.75,
          boxSizing: 'border-box',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
        },
      }}
    >
      <Stack spacing={1.5}>
        <Typography id={`${id}-title`} sx={{ ...reportSectionTitleSx, fontSize: '0.95rem' }}>Custom period</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            fullWidth size="small" type="date" label={fromLabel} value={dateFrom}
            InputLabelProps={{ shrink: true }} inputProps={{ max: businessTodayIso() }}
            onChange={(event) => onDateFromChange(event.target.value)}
            sx={dateFieldSx}
          />
          <TextField
            fullWidth size="small" type="date" label={toLabel} value={dateTo}
            InputLabelProps={{ shrink: true }} inputProps={{ min: dateFrom || undefined, max: businessTodayIso() }}
            onChange={(event) => onDateToChange(event.target.value)}
            sx={dateFieldSx}
          />
        </Stack>
        <Typography sx={{ ...reportBodySx, fontSize: '0.72rem' }}>{description}</Typography>
        {error && <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.76rem' }}>{error}</Alert>}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1.25, borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>
          <Button
            size="small" variant="outlined" onClick={onClose}
            sx={{ ...actionSx, color: '#475569', borderColor: '#cbd5e1', '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' } }}
          >
            Cancel
          </Button>
          <Button
            size="small" variant="contained" disableElevation onClick={onApply}
            sx={{ ...actionSx, bgcolor: '#0f172a', color: '#fff', '&:hover': { bgcolor: '#1e293b' } }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
